import { db } from "@/firebaseApp";
import { and, collection, onSnapshot, or, deleteDoc, doc, setDoc, query, updateDoc, where, serverTimestamp } from "firebase/firestore";

import { storage } from "@/firebaseApp";
import { ref as storageRef, uploadBytesResumable } from "firebase/storage";

import links from "./projects/links.js";

function projectSetup(getters, commit, project) {
    if (project.serviceName == null) {
        const service = getters['services/id'](project.service)
        project.serviceName = service?.name
    }

    if (project.worker != null) {
        const worker = getters['workers/id'](project.worker)
        project.workerName = worker?.name
    }

    if (project.user != null) {
        const consumer = getters['consumers/id'](project.user)
        project.consumerName = consumer?.name
    }

    commit("add", project)
    return
}

const projects = {
    namespaced: true,
    state: {
        all: [],
        created: null,
        project: null,
        unsubs: [],
        uploaded: [],
        links: [],
    },
    getters: {
        assigned(state) {
            return state.all.filter(({ worker, done }) => worker != null && !done)
        },
        consumer: (state) => (consumerId) => {
            return state.all.filter(({ user }) => user == consumerId)
        },
        done(state) {
            return state.all.filter(({ done }) => done)
        },
        id: (state) => (projectId) => {
            return state.all.find(({ id }) => id == projectId)
        },
        service: (state) => (serviceId) => {
            return state.all.filter(({ service }) => service == serviceId)
        },
        sorted(state) {
            return [...state.all].sort((a, b) => {
                if (a.createdAt == null) return 1
                if (b.createdAt == null) return -1
                return a.createdAt - b.createdAt
            })
        },
        unassigned(state) {
            return state.all.filter(({ worker, done }) => worker == null && !done)
        },
        worker: (state) => (workerId) => {
            return state.all.filter(({ worker }) => worker == workerId)
        },
    },

    mutations: {
        add(state, project) {
            const index = state.all.findIndex(({ id }) => project.id === id)
            if (index < 0) {
                state.all.push(project);
                return
            }
            state.all.splice(index, 1, project)
        },

        all(state, projects) {
            state.all = projects;
        },

        addUnsub(state, unsub) {
            state.unsubs.push(unsub)
        },

        clearUploaded(state) {
            state.uploaded = []
        },

        delete(state, project) {
            const index = state.all.findIndex(({ id }) => project.id === id)
            if (index < 0) {
                return
            }
            state.all.splice(index, 1)
        },

        project(state, project) {
            state.project = project;
        },

        setCreated(state, id) {
            state.created = id;
        },

        unsub(state) {
            while (state.unsubs.length) {
                let unsub = state.unsubs.pop()
                unsub()
            }
        },

        uploaded(state, file) {
            state.uploaded.push(file)
        },
    },

    actions: {
        toggleDone({ rootState }, project ) {
            const instanceId = rootState.instanceId

            try {
                const projectRef = doc(collection(db, `instances/${instanceId}/projects`), project.id)
                updateDoc(projectRef, {
                    done: !project.done
                })
            } catch (error) {
                console.log(error)
            }
        },
        async assignToSelf({ rootState }, projectId) {
            const instanceId = rootState.instanceId
            const currentUser = rootState.users.currentUser
            const role = rootState.users.role

            if (instanceId == null) {
                return
            }
            if (role !== 'worker') {
                return
            }
            if (currentUser == null) {
                return
            }

            try {
                const projectRef = doc(collection(db, `instances/${instanceId}/projects`), projectId)
                await updateDoc(projectRef, {
                    worker: rootState.users.userInfo.worker
                })
            } catch (error) {
                console.log(error)
            }
        },

        async getAll({ commit, rootState, rootGetters }) {
            const instanceId = rootState.instanceId
            const currentUser = rootState.users.currentUser
            const role = rootState.users.role

            if (instanceId == null || currentUser == null || role == null) {
                return
            }

            commit('unsub')

            let projectsRef = collection(db, `instances/${instanceId}/projects`)
            let projectQuery = projectsRef

            try {
                if (role == 'consumer') {
                    projectQuery = query(projectsRef, or(
                        where('consumer', "==", rootState.users.userInfo.consumer),
                        where('user', "==", rootState.users.userInfo.consumer) // TODO: deprecated attribute; 
                    ))
                } else if (role == 'worker') {
                    projectQuery = query(projectsRef,
                        or(
                            and(
                                where('service', "==", rootState.users.userInfo.service),
                                where('worker', "==", null),
                            ),
                            and(
                                where('service', "==", rootState.users.userInfo.service),
                                where('worker', "==", rootState.users.userInfo.worker),
                            ),
                        ),
                    )
                }
                let unsub = onSnapshot(projectQuery, (querySnapshot) => {
                    querySnapshot.docChanges().forEach((change) => {
                        const doc = change.doc;
                        let data = doc.data()
                        let project = { ...data, id: doc.id }
                        switch (change.type) {
                            case 'added':
                                projectSetup(rootGetters, commit, project)
                                break
                            case 'modified':
                                projectSetup(rootGetters, commit, project)
                                break
                            case "removed":
                                commit("delete", project);
                                break;
                            default:
                                break;
                        }
                    })
                })
                commit('addUnsub', unsub)
            } catch (error) {
                console.log("ERROR TO GET ALL PROJECTS: " + error);
            }
        },

        sendMessage({ rootState }, { projectId, message }) {
            const instanceId = rootState.instanceId
            const currentUser = rootState.users.currentUser

            if (currentUser == null) return
            if (instanceId == null) return

            try {
                const projectRef = doc(collection(db, `instances/${instanceId}/projects/${projectId}/messages`))
                setDoc(projectRef, {
                    message: message.replaceAll("\n", "<br>"),
                    user: currentUser.uid,
                    createdAt: serverTimestamp(),
                })
            } catch (error) {
                console.log(error)
            }
        },

        async sendFile({ rootState, commit }, { projectId, file }) {
            const instanceId = rootState.instanceId
            const currentUser = rootState.users.currentUser

            if (currentUser == null || instanceId == null) {
                return
            }

            try {
                const messageRef = doc(collection(db, `instances/${instanceId}/projects/${projectId}/messages`))
                const uploadRef = storageRef(storage, `${instanceId}/projects/${projectId}/${messageRef.id}/${file.name}`)
                const uploadTask = uploadBytesResumable(uploadRef, file)
                uploadTask.on('state_changed',
                    (snapshot) => {
                        console.log(snapshot.bytesTransferred / snapshot.totalBytes * 100)
                        console.log(snapshot.state)
                    },
                    (error) => {
                        console.log(error)
                    },
                    () => {
                        setDoc(messageRef, {
                            file: file.name,
                            user: currentUser.uid,
                            createdAt: serverTimestamp(),
                        })
                        commit('uploaded', file)
                    },
                )
            } catch (error) {
                console.log(error)
            }
        },

        async create({ rootState, dispatch, commit }, { name, serviceId, description, files }) {
            const instanceId = rootState.instanceId
            const userInfo = rootState.users.userInfo
            console.log('USERINFO: ',userInfo)

            if (userInfo == null) {
                return
            }
            if (instanceId == null) {
                return
            }

            try {
                const projectRef = doc(collection(db, `instances/${instanceId}/projects`))
                await setDoc(projectRef, {
                    name,
                    user: userInfo.consumer,
                    consumer: userInfo.consumer,
                    service: serviceId,
                    description: description.replaceAll("\n", "<br>"),
                    worker: null,
                })
                commit('setCreated', projectRef.id)
                if (files == null) {
                    return
                }
                for (const file of files) {
                    dispatch('sendFile', { projectId: projectRef.id, file })
                }
            } catch (error) {
                console.log(error)
            }
        },

        async addMessagesCollection({ rootState }, { userId, createdAt, messageSent, projectId }) {
            const instanceId = rootState.instanceId

            if (instanceId == null) return

            try {
                const messagesRef = doc(collection(db, `instances/${instanceId}/projects/${projectId}/messages`))

                await setDoc(messagesRef, {
                    createdAt,
                    userId,
                    messageSent,
                    // messageId,
                })
            } catch (error) {
                console.log(error)
            }
        },

        async addUsersCollection({ rootState }, { projectId, lastReadAt, userId }) {
            const instanceId = rootState.instanceId

            if (instanceId == null) return
            if (lastReadAt == null) return

            try {
                const usersRef = doc(collection(db, `instances/${instanceId}/projects/${projectId}/users`))

                await setDoc(usersRef, {
                    userId,
                    lastReadAt
                })

            } catch (error) {
                console.log(error)
            }
        },

        async delete({ rootState, commit }, id) {
            const instanceId = rootState.instanceId
            if (instanceId == null) return

            try {
                const projectRef = doc(db, `instances/${instanceId}/projects`, id)
                await deleteDoc(projectRef)
                commit('delete', id)
            } catch (error) {
                console.log('ERROR: ' + error)
            }
        },
    },

    modules: {
        links,
    },
}
export default projects
